/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
} from '@mui/material';

import { DefaultClusterLimits } from 'shared';

import { Namespaces } from '../../../../i18n';
import { useFormStyles } from '../../../../styles/form';
import { ObjectMap } from '../../../../types/utils/ObjectMap';
import {
  ApiValidationGetFieldError,
  ApiValidationHasFieldError,
  ApiValidationResetFieldError,
} from '../../../../utils/useApiValidation';
import { useValidationTranslation } from '../../../../utils/useValidationTranslation';
import { SystemConfigurationFormValues } from '../../SystemConfigurationPage.types';
import { useSystemConfigurationFormStyles } from './SystemConfigurationForm.styles';

export type SystemConfigurationFormProps = FormikProps<SystemConfigurationFormValues> & {
  isFetching: boolean;
  hasFieldApiError: ApiValidationHasFieldError<ObjectMap>;
  getFieldApiError: ApiValidationGetFieldError<ObjectMap>;
  resetFieldApiError: ApiValidationResetFieldError<ObjectMap>;
};

export function SystemConfigurationForm(props: SystemConfigurationFormProps) {
  const { vtr } = useValidationTranslation();
  const formClasses = useFormStyles();
  const classes = useSystemConfigurationFormStyles();
  const { t } = useTranslation(Namespaces.Admin);

  const {
    touched,
    values,
    errors,
    handleChange,
    handleBlur,
    hasFieldApiError,
    getFieldApiError,
    isFetching,
    setFieldValue,
  } = props;

  const hasError = useCallback(
    (name: keyof SystemConfigurationFormValues): boolean => {
      return !!touched[name] && (!!errors[name] || hasFieldApiError(name));
    },
    [errors, hasFieldApiError, touched],
  );

  const getError = useCallback(
    (name: keyof SystemConfigurationFormValues): string => {
      const error = errors[name] || getFieldApiError(name);
      return error ? error : '';
    },
    [errors, getFieldApiError],
  );

  const handleShowCostChange = (e: React.ChangeEvent<any>) => {
    void setFieldValue('showInstanceCost', e.target.checked);
    void setFieldValue('showSupportCost', e.target.checked);
    handleChange(e);
  };

  useEffect(() => {
    if (!values.showInstanceCost && !values.showSupportCost) {
      void setFieldValue('showCost', false);
    }
  }, [values.showInstanceCost, values.showSupportCost, setFieldValue]);

  return (
    <>
      <div className={cx(classes.groupContainer, formClasses.widthMedium)}>
        <div className={classes.checkboxes}>
          <FormControlLabel
            name="allowServiceCreation"
            checked={values.allowServiceCreation}
            onChange={handleChange}
            control={<Checkbox color="primary" />}
            label={t('system_configuration.form.allow_service_creation')}
            labelPlacement="end"
            disabled={isFetching}
          />
        </div>
        <div className={classes.checkboxes}>
          <FormControlLabel
            name="showCost"
            checked={values.showCost}
            onChange={handleShowCostChange}
            control={<Checkbox color="primary" />}
            label={t('system_configuration.form.show_cost')}
            labelPlacement="end"
            disabled={isFetching}
          />
        </div>
        <Collapse in={values.showCost} classes={{ wrapperInner: classes.subOptions }}>
          <FormControl>
            <FormControlLabel
              name="showInstanceCost"
              checked={values.showInstanceCost}
              onChange={handleChange}
              control={<Checkbox color="primary" />}
              label={t('system_configuration.form.show_instance_cost')}
              labelPlacement="end"
              disabled={isFetching}
            />
            <FormHelperText className={classes.subOptionTooltip}>
              {t('system_configuration.form.instance_cost_tooltip')}
            </FormHelperText>
          </FormControl>
          <FormControl>
            <FormControlLabel
              name="showSupportCost"
              checked={values.showSupportCost}
              onChange={handleChange}
              control={<Checkbox color="primary" />}
              label={t('system_configuration.form.show_support_cost')}
              labelPlacement="end"
              disabled={isFetching}
              sx={{ mr: 0 }}
            />
            <FormHelperText className={classes.subOptionTooltip}>
              {t('system_configuration.form.support_cost_tooltip')}
            </FormHelperText>
          </FormControl>
        </Collapse>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthMedium)}
          error={hasError('currency')}
          variant="outlined"
        >
          <InputLabel htmlFor="currency">{t('system_configuration.form.currency')}</InputLabel>
          <OutlinedInput
            name="currency"
            label={t('system_configuration.form.currency')}
            value={values.currency}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
          />
          {hasError('currency') && (
            <FormHelperText>
              {vtr(getError('currency'), {
                field: t('system_configuration.form.currency'),
                length: 3,
              })}
            </FormHelperText>
          )}
          <FormHelperText sx={{ color: 'rgba(0,0,0,0.6) !important' }}>
            {t('system_configuration.form.currency_tooltip')}
          </FormHelperText>
        </FormControl>
      </div>
      <Grid item classes={{ root: 'd-flex' }} className={cx(formClasses.widthMedium, classes.group)}>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull, classes.formControl)}
          error={hasError('minCpuLimit')}
          variant="outlined"
        >
          <InputLabel htmlFor="minCpuLimit">{t('system_configuration.form.min_cpu_value')}</InputLabel>
          <OutlinedInput
            name="minCpuLimit"
            label={t('system_configuration.form.min_cpu_value')}
            value={values.minCpuLimit}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
            placeholder={t('system_configuration.form.placeholder')}
          />
          {hasError('minCpuLimit') && (
            <FormHelperText>
              {vtr(getError('minCpuLimit'), {
                field: t('system_configuration.form.min_cpu_value'),
                moreThan: DefaultClusterLimits.Cpu.Min,
              })}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull, classes.formControl)}
          error={hasError('maxCpuLimit')}
          variant="outlined"
        >
          <InputLabel htmlFor="maxCpuLimit">{t('system_configuration.form.max_cpu_value')}</InputLabel>
          <OutlinedInput
            name="maxCpuLimit"
            label={t('system_configuration.form.max_cpu_value')}
            value={values.maxCpuLimit}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
            placeholder={t('system_configuration.form.placeholder')}
            data-cy="system-config-max-cpu-limit"
          />
          {hasError('maxCpuLimit') && (
            <FormHelperText>
              {vtr(getError('maxCpuLimit'), {
                field: t('system_configuration.form.max_cpu_value'),
                moreThan: values.minCpuLimit || '',
              })}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item classes={{ root: 'd-flex' }} className={cx(formClasses.widthMedium, classes.group)}>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull, classes.formControl)}
          error={hasError('minMemoryLimit')}
          variant="outlined"
        >
          <InputLabel htmlFor="minMemoryLimit">{t('system_configuration.form.min_memory_value')}</InputLabel>
          <OutlinedInput
            name="minMemoryLimit"
            label={t('system_configuration.form.min_memory_value')}
            value={values.minMemoryLimit}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
            placeholder={t('system_configuration.form.placeholder')}
          />
          {hasError('minMemoryLimit') && (
            <FormHelperText>
              {vtr(getError('minMemoryLimit'), {
                field: t('system_configuration.form.min_memory_value'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull, classes.formControl)}
          error={hasError('maxMemoryLimit')}
          variant="outlined"
        >
          <InputLabel htmlFor="maxMemoryLimit">{t('system_configuration.form.max_memory_value')}</InputLabel>
          <OutlinedInput
            name="maxMemoryLimit"
            label={t('system_configuration.form.max_memory_value')}
            value={values.maxMemoryLimit}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
            placeholder={t('system_configuration.form.placeholder')}
            data-cy="system-config-max-memory-limit"
          />
          {hasError('maxMemoryLimit') && (
            <FormHelperText>
              {vtr(getError('maxMemoryLimit'), {
                field: t('system_configuration.form.max_memory_value'),
              })}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item classes={{ root: 'd-flex' }} className={cx(formClasses.widthMedium, classes.group)}>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull, classes.formControl)}
          error={hasError('minStorageLimit')}
          variant="outlined"
        >
          <InputLabel htmlFor="minStorageLimit">{t('system_configuration.form.min_storage_value')}</InputLabel>
          <OutlinedInput
            name="minStorageLimit"
            label={t('system_configuration.form.min_storage_value')}
            value={values.minStorageLimit}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
            placeholder={t('system_configuration.form.placeholder')}
          />
          {hasError('minStorageLimit') && (
            <FormHelperText>
              {vtr(getError('minStorageLimit'), {
                field: t('system_configuration.form.min_storage_value'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull, classes.formControl)}
          error={hasError('maxStorageLimit')}
          variant="outlined"
        >
          <InputLabel htmlFor="maxStorageLimit">{t('system_configuration.form.max_storage_value')}</InputLabel>
          <OutlinedInput
            name="maxStorageLimit"
            label={t('system_configuration.form.max_storage_value')}
            value={values.maxStorageLimit}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
            placeholder={t('system_configuration.form.placeholder')}
            data-cy="system-config-max-storage-limit"
          />
          {hasError('maxStorageLimit') && (
            <FormHelperText>
              {vtr(getError('maxStorageLimit'), {
                field: t('system_configuration.form.max_storage_value'),
              })}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item classes={{ root: 'd-flex' }} className={cx(formClasses.widthMedium, classes.group)}>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull, classes.formControl)}
          error={hasError('minReplicasLimit')}
          variant="outlined"
        >
          <InputLabel htmlFor="minReplicasLimit">{t('system_configuration.form.min_replicas_value')}</InputLabel>
          <OutlinedInput
            name="minReplicasLimit"
            label={t('system_configuration.form.min_replicas_value')}
            value={values.minReplicasLimit}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
            type="number"
            inputProps={{
              min: DefaultClusterLimits.Replicas.Min,
              max: values.maxReplicasLimit,
            }}
          />
          {hasError('minReplicasLimit') && (
            <FormHelperText>
              {vtr(getError('minReplicasLimit'), {
                field: t('system_configuration.form.min_replicas_value'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull, classes.formControl)}
          error={hasError('maxReplicasLimit')}
          variant="outlined"
        >
          <InputLabel htmlFor="maxReplicasLimit">{t('system_configuration.form.max_replicas_value')}</InputLabel>
          <OutlinedInput
            name="maxReplicasLimit"
            label={t('system_configuration.form.max_replicas_value')}
            value={values.maxReplicasLimit}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
            type="number"
            inputProps={{
              min: values.minReplicasLimit,
              max: DefaultClusterLimits.Replicas.Max,
            }}
          />
          {hasError('maxReplicasLimit') && (
            <FormHelperText>
              {vtr(getError('maxReplicasLimit'), {
                field: t('system_configuration.form.max_replicas_value'),
              })}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasError('cpuPricePerMonth')}
        variant="outlined"
      >
        <InputLabel htmlFor="cpuPricePerMonth">{t('system_configuration.form.cpu_price_per_month')}</InputLabel>
        <OutlinedInput
          name="cpuPricePerMonth"
          label={t('system_configuration.form.cpu_price_per_month')}
          value={values.cpuPricePerMonth}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isFetching}
        />
        {hasError('cpuPricePerMonth') && (
          <FormHelperText>
            {vtr(getError('cpuPricePerMonth'), {
              field: t('system_configuration.form.cpu_price_per_month'),
            })}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasError('memoryPricePerMonth')}
        variant="outlined"
      >
        <InputLabel htmlFor="memoryPricePerMonth">{t('system_configuration.form.memory_price_per_month')}</InputLabel>
        <OutlinedInput
          name="memoryPricePerMonth"
          label={t('system_configuration.form.memory_price_per_month')}
          value={values.memoryPricePerMonth}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isFetching}
        />
        {hasError('memoryPricePerMonth') && (
          <FormHelperText>
            {vtr(getError('memoryPricePerMonth'), {
              field: t('system_configuration.form.memory_price_per_month'),
            })}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasError('storagePricePerMonth')}
        variant="outlined"
      >
        <InputLabel htmlFor="storagePricePerMonth">{t('system_configuration.form.storage_price_per_month')}</InputLabel>
        <OutlinedInput
          name="storagePricePerMonth"
          label={t('system_configuration.form.storage_price_per_month')}
          value={values.storagePricePerMonth}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isFetching}
        />
        {hasError('storagePricePerMonth') && (
          <FormHelperText>
            {vtr(getError('storagePricePerMonth'), {
              field: t('system_configuration.form.storage_price_per_month'),
            })}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasError('cpuRequestToLimitMultiplier')}
        variant="outlined"
      >
        <InputLabel htmlFor="cpuRequestToLimitMultiplier">
          {t('system_configuration.form.cpu_request_to_limit_multiplier')}
        </InputLabel>
        <OutlinedInput
          name="cpuRequestToLimitMultiplier"
          type="number"
          label={t('system_configuration.form.cpu_request_to_limit_multiplier')}
          value={values.cpuRequestToLimitMultiplier}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isFetching}
          inputProps={{ min: 1, step: 0.1 }}
        />
        {hasError('cpuRequestToLimitMultiplier') && (
          <FormHelperText>
            {vtr(getError('cpuRequestToLimitMultiplier'), {
              field: t('system_configuration.form.cpu_request_to_limit_multiplier'),
            })}
          </FormHelperText>
        )}
      </FormControl>
    </>
  );
}
